import { NotificationTwoTone } from '@ant-design/icons';
import { Button, Divider, Image, List, Modal, Space, Typography } from 'antd';
import axios, { type AxiosResponse } from 'axios';
import React, { type CSSProperties, Fragment, useState } from 'react';

import { asyncErrorHandler } from '@/utils/asyncErrorHandler';

import { ImagePlaceHolderIcon } from '../iconComponents';
import type {
  CleanKindFetchResponse,
  CleanKindResult,
  TimeUnitType,
} from '../sharedTypes';
import styles from './index.module.scss';

type CleanKindDetailProps = {
  record: CleanKindResult;
  mustCancelBeforeValues: {
    value: number | undefined;
    unit: TimeUnitType | undefined;
  };
};

const { Title, Text } = Typography;
const modalBodyStyles: CSSProperties = {
  maxHeight: '95vh',
  overflowY: 'scroll',
};

const CleanKindDetail = ({
  record,
  mustCancelBeforeValues,
}: CleanKindDetailProps) => {
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [data, setData] = useState<CleanKindFetchResponse>();

  const getData = async (cleanKindId: string) => {
    try {
      const res: AxiosResponse<CleanKindFetchResponse> = await axios({
        method: 'GET',
        url: '/ezcon/api/fetchCleanKind',
        headers: {
          authorization: localStorage.getItem('feathers-jwt'),
        },
        params: {
          cleanKindId,
        },
      });

      const paidItems = res.data.cleanServiceItemList.filter(
        (item) => item.priceInfo.kind !== 'Free'
      );
      const newData = {
        ...res.data,
        cleanServiceItemList: paidItems,
      };
      setData(newData);
      setIsOpenModal(true);
    } catch (error) {
      const errObj = error.response.data;
      asyncErrorHandler(errObj, '資料載入失敗，請稍後再試');
    }
  };

  const { value, unit } = mustCancelBeforeValues;
  const chineseUnit = unit === 'day' ? '天' : unit === 'week' ? '週' : '月';

  return (
    <>
      <Button type="primary" onClick={() => getData(record.cleanKindId)}>
        查看
      </Button>
      <Modal
        open={isOpenModal}
        footer={null}
        centered
        destroyOnClose
        width={550}
        bodyStyle={modalBodyStyles}
        onCancel={() => setIsOpenModal(false)}
      >
        {data && (
          <>
            <div className={styles['mt-40']}>
              {data.picture ? (
                <Image src={data.picture} />
              ) : (
                <ImagePlaceHolderIcon />
              )}
            </div>
            <Divider />
            <Title level={3}>{data.name}</Title>
            <Divider />
            <Space direction="vertical" size="large">
              <div className={styles.sectionTitle}>
                <NotificationTwoTone className={styles.icon} />
                <h3 className={styles.titleText}>服務內容：</h3>
              </div>
              <Space direction="vertical">
                <Text type="secondary">服務介紹</Text>
                <p className={styles.paragraph}>{data.description}</p>
              </Space>
              <Space direction="vertical">
                <Text type="secondary">費用包含</Text>
                <p className={styles.paragraph}>{data.included}</p>
              </Space>
            </Space>
            <Divider />
            <Space direction="vertical" size="large">
              <div className={styles.sectionTitle}>
                <NotificationTwoTone className={styles.icon} />
                <h3 className={styles.titleText}>服務須知：</h3>
              </div>
              <Space direction="vertical">
                <Text type="secondary">注意事項</Text>
                <p className={styles.paragraph}>{data.warning}</p>
              </Space>
              <Space direction="vertical">
                <Text type="secondary">退費規則</Text>
                <p
                  className={styles.paragraph}
                >{`預約日期前${value}${chineseUnit}可取消預約，並全額退費。`}</p>
              </Space>
            </Space>
            <Divider />
            <div className={styles.sectionTitle}>
              <NotificationTwoTone className={styles.icon} />
              <h3 className={styles.titleText}>清潔項目：</h3>
            </div>
            {data.cleanServiceItemList.length !== 0 ? (
              <div className={styles.bottomGridContainer}>
                <Text type="secondary">項目</Text>
                <Text type="secondary">價格</Text>
                <Text type="secondary">單筆預約下限與上限數量</Text>
                {
                  <>
                    {data.cleanServiceItemList.map((item) => {
                      const { name, priceInfo } = item;
                      switch (priceInfo.kind) {
                        case 'TaiwanPay': {
                          return (
                            <Fragment key={item._id}>
                              <span>{name}</span>
                              <span>{`${priceInfo.taiwanPayPrice}元`}</span>
                              <span>{`${priceInfo.reservationLowerLimit} - ${priceInfo.reservationUpperLimit}`}</span>
                            </Fragment>
                          );
                        }
                        case 'Point': {
                          return (
                            <Fragment key={item._id}>
                              <span>{name}</span>
                              <span>{`${priceInfo.pointPrice}點`}</span>
                              <span>{`${priceInfo.reservationLowerLimit} - ${priceInfo.reservationUpperLimit}`}</span>
                            </Fragment>
                          );
                        }
                        case 'Both': {
                          return (
                            <Fragment key={item._id}>
                              <span>{name}</span>
                              <span>{`${priceInfo.taiwanPayPrice}元`}</span>
                              <span>{`${priceInfo.reservationLowerLimit} - ${priceInfo.reservationUpperLimit}`}</span>
                            </Fragment>
                          );
                        }
                        default:
                          return <></>;
                      }
                    })}
                  </>
                }
              </div>
            ) : (
              <List />
            )}
          </>
        )}
      </Modal>
    </>
  );
};

export default CleanKindDetail;
